import { Head, useForm, Link } from '@inertiajs/react'
import AuthLayout from '../../components/AuthLayout'

interface Props {
  errors: {
    base?: string
  }
  csrf_token?: string
}

function LoginForm({ errors, data, setData, handleSubmit, processing, idPrefix = '', csrfToken }: {
  errors: Props['errors']
  data: { user: { email: string; password: string; remember_me: boolean } }
  setData: (key: 'user', value: { email: string; password: string; remember_me: boolean }) => void
  handleSubmit: (e: React.FormEvent) => void
  processing: boolean
  idPrefix?: string
  csrfToken?: string
}) {
  const eid = (name: string) => idPrefix ? `${name}-${idPrefix}` : name

  return (
    <>
      {errors.base && (
        <div
          className="mb-6 p-3 rounded"
          style={{
            background: 'rgba(237, 28, 36, 0.08)',
            borderLeft: '3px solid #ED1C24',
          }}
        >
          <p className="font-sans text-base text-text-primary">{errors.base}</p>
        </div>
      )}

      <p className="font-serif italic text-2xl text-text-primary text-center mb-8"
         style={{ color: '#1B1B1B' }}>
        Welcome back.
      </p>

      <form action="/users/auth/auth0" method="post" className="mb-0">
        {csrfToken && <input type="hidden" name="authenticity_token" value={csrfToken} />}
        <button
          type="submit"
          className="w-full flex items-center justify-center gap-2.5 bg-[#351E1D] hover:bg-[#2a1715] text-white font-bold text-sm uppercase tracking-wider py-3.5 px-6 rounded-button transition-colors duration-200"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 2.18l7 3.12v4.7c0 4.67-3.13 9.06-7 10.2-3.87-1.14-7-5.53-7-10.2V6.3l7-3.12z"/>
          </svg>
          Sign in with Auth0
        </button>
      </form>

      <div className="relative my-6 flex items-center">
        <div className="flex-grow border-t border-charcoal/15" />
        <span className="mx-4 font-sans text-sm text-text-muted">or continue with email</span>
        <div className="flex-grow border-t border-charcoal/15" />
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label htmlFor={eid('email')} className="block font-sans text-xs font-bold uppercase tracking-[0.1em] text-text-primary mb-2">
            Email
          </label>
          <input
            id={eid('email')}
            type="email"
            value={data.user.email}
            onChange={e => setData('user', { ...data.user, email: e.target.value })}
            className="input w-full"
            placeholder="you@example.com"
            required
            autoComplete="email"
          />
        </div>

        <div>
          <label htmlFor={eid('password')} className="block font-sans text-xs font-bold uppercase tracking-[0.1em] text-text-primary mb-2">
            Password
          </label>
          <input
            id={eid('password')}
            type="password"
            value={data.user.password}
            onChange={e => setData('user', { ...data.user, password: e.target.value })}
            className="input w-full"
            placeholder="Enter your password"
            required
            autoComplete="current-password"
          />
        </div>

        <div className="flex items-center justify-between mt-2 mb-1">
          <div className="flex items-center">
            <input
              id={eid('remember_me')}
              type="checkbox"
              checked={data.user.remember_me}
              onChange={e => setData('user', { ...data.user, remember_me: e.target.checked })}
              className="h-4 w-4 rounded border-stone text-pinnacle-red focus:ring-charcoal/30 cursor-pointer"
            />
            <label htmlFor={eid('remember_me')} className="ml-2 text-base font-sans text-text-primary cursor-pointer select-none">
              Remember me
            </label>
          </div>
          <Link href="/users/password/new" className="text-base font-sans text-text-primary hover:underline">
            Forgot password?
          </Link>
        </div>

        <button
          type="submit"
          disabled={processing}
          className="w-full bg-pinnacle-red hover:bg-pinnacle-red-dark text-white font-bold text-sm uppercase tracking-wider py-3.5 px-6 rounded-button transition-colors duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {processing ? 'SIGNING IN...' : 'SIGN IN'}
        </button>
      </form>

      <div className="mt-6 text-center">
        <p className="font-sans text-base text-text-muted">
          Don&apos;t have an account?{' '}
          <Link href="/users/sign_up" className="text-text-primary hover:underline font-medium">
            Create one
          </Link>
        </p>
      </div>

      <div className="mt-6 text-center">
        <p className="font-sans text-sm text-text-muted">
          Need help?{' '}
          <a href="mailto:climb@pinnacle.guide" className="text-text-primary hover:underline">
            climb@pinnacle.guide
          </a>
        </p>
      </div>
    </>
  )
}

export default function Login({ errors, csrf_token }: Props) {
  const { data, setData, post, processing, transform } = useForm({
    user: {
      email: '',
      password: '',
      remember_me: false as boolean,
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // Send the freshly-rendered CSRF token as an explicit body param so the
    // login POST never relies on the XSRF-TOKEN cookie, which goes stale after
    // logout rotates the session. Rails accepts the request if *any* supplied
    // token is valid, so this guarantees the real session is preserved.
    transform((current) => ({ ...current, authenticity_token: csrf_token ?? '' }))
    post('/users/sign_in')
  }

  return (
    <>
      <Head title="Sign In - Pinnacle" />
      <AuthLayout>
        <LoginForm
          errors={errors}
          data={data}
          setData={setData}
          handleSubmit={handleSubmit}
          processing={processing}
          csrfToken={csrf_token}
        />
      </AuthLayout>
    </>
  )
}
